import FormatHelper from '@/helpers/format-helper';
import {opcionalesDelTramo} from '@/helpers/tramo-helper';

/**
 * N-288 · T3 (decisiones 1B · 2A · 3A de Bernardo, 7-oct-2026) · Al pulsar GUARDAR, una ventana con lo
 * que se QUITA y lo que CAMBIA DE PRECIO: «Volver y revisar» / «Guardar así». Si no cambia nada, se
 * guarda sin preguntar. La lista la monta el panel con lo que ya le dicen los catálogos (ninguna
 * petición nueva): cada pestaña cuenta lo suyo con `cambiosAlGuardar()` y el formulario lo junta.
 */

/** Lo que se quita al guardar, con su motivo («llega a 55») y la causa, que decide el título. */
export interface Quitado {
	texto: string;
	motivo: string;
	causa: 'personas' | 'fechas' | 'dias';
}

/** Lo que pasa a otra tarifa al guardar, con el precio de antes y el de ahora. */
export interface Cambio {
	texto: string;
	antes: any;
	ahora: any;
	unidad: string;
}

export interface CambiosAlGuardar {
	quitan: Quitado[];
	cambian: Cambio[];
}

export function sinCambios(): CambiosAlGuardar {
	return { quitan: [], cambian: [] };
}

/** Lo de varias pestañas, en su orden. */
export function juntar(partes: CambiosAlGuardar[]): CambiosAlGuardar {
	return partes.reduce((todo, parte) => ({
		quitan: todo.quitan.concat(parte.quitan),
		cambian: todo.cambian.concat(parte.cambian),
	}), sinCambios());
}

/** Un importe como en la maqueta: «58», «2,50», «1.050». */
export function importe(valor: any): string {
	const [entero, decimales] = Number(valor).toFixed(2).split('.');
	const conMiles = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
	return decimales === '00' ? conMiles : conMiles + ',' + decimales;
}

/** «Día 3, mañana · Kayak» (la media jornada) o «Día 2 · Bosque del Spree» (el día completo). */
export function etiquetaDelHueco(indiceDelDia: number, parte: string, actividad: string): string {
	const cuando = parte === 'day_part_1' ? ', mañana' : (parte === 'day_part_2' ? ', tarde' : '');
	return `Día ${indiceDelDia + 1}${cuando} · ${actividad}`;
}

/** Por qué una actividad no tiene precio para el grupo: «llega a 55» o «es desde 20» (`people_range`). */
export function motivoPorPersonas(rango: any, personas: number): string {
	if (rango && Number(rango.max) < personas) {
		return 'llega a ' + rango.max;
	}
	if (rango && Number(rango.min) > personas) {
		return 'es desde ' + rango.min;
	}
	return '';
}

/**
 * Si una actividad queda fuera de su ventana de fechas ese día, el motivo («solo hasta el 31/05/2027»);
 * si no (o sin fecha), ''. Es la regla del catálogo (`PublicActivityResourceCollection`, M-32): cada extremo vale
 * solo si está puesto.
 */
export function motivoPorFechas(desde: any, hasta: any, fecha: string): string {
	if (!fecha) {
		return '';
	}
	const dia = (valor) => String(valor || '').substring(0, 10);
	const fuera = (desde && fecha < dia(desde)) || (hasta && fecha > dia(hasta));
	if (!fuera) {
		return '';
	}
	if (desde && hasta) {
		return 'solo del ' + FormatHelper.formatDate(dia(desde)) + ' al ' + FormatHelper.formatDate(dia(hasta));
	}
	return hasta ? 'solo hasta el ' + FormatHelper.formatDate(dia(hasta)) : 'solo desde el ' + FormatHelper.formatDate(dia(desde));
}

/** Un nombre de tarifa para emparejar: sin mayúsculas, acentos ni espacios de más («Visita Guiada» = «visita guiada»). */
function nombreComparable(nombre: any): string {
	return String(nombre || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Varias tarifas sueltas como una sola línea: sus nombres con «+», la suma y `group` si todas lo comparten. */
function enUnaLinea(tarifas: any[]): any {
	const grupos = tarifas.map((tarifa) => Number(tarifa.group) === 1);
	return {
		name: tarifas.map((tarifa) => tarifa.name).join(' + '),
		price: tarifas.reduce((suma, tarifa) => suma + Number(tarifa.price), 0),
		group: grupos.every((g) => g) ? 1 : 0,
		mezcla: grupos.some((g) => g) && !grupos.every((g) => g),
	};
}

/**
 * Las tarifas OBLIGATORIAS de antes ({id, name, price, group}) frente a las de ahora. La que sigue con su
 * id no cambia. Las demás se emparejan por nombre (sin mayúsculas, acentos ni espacios de más) y cambian
 * de precio si cuestan otra cosa. Lo que queda sin pareja a los dos lados (otro tramo con otros nombres:
 * «Conductor» → «Dos conductores») sale en una línea, con la suma de antes y la de ahora.
 */
export function tarifasQueCambian(antes: any[], ahora: any[]): any[] {
	const cambios: any[] = [];
	const viejas = (antes || []).filter((vieja) => !(ahora || []).some((tarifa) => tarifa.id == vieja.id));
	const nuevas = (ahora || []).filter((tarifa) => !(antes || []).some((vieja) => vieja.id == tarifa.id));
	const sueltasNuevas: any[] = [];
	for (const tarifa of nuevas) {
		const indice = viejas.findIndex((vieja) => nombreComparable(vieja.name) !== ''
			&& nombreComparable(vieja.name) === nombreComparable(tarifa.name));
		if (indice < 0) {
			sueltasNuevas.push(tarifa);
			continue;
		}
		const pareja = viejas.splice(indice, 1)[0];
		if (Number(pareja.price) !== Number(tarifa.price)) {
			cambios.push({ antes: pareja, ahora: tarifa });
		}
	}
	if (viejas.length && sueltasNuevas.length) {
		const antesJuntas = enUnaLinea(viejas);
		const ahoraJuntas = enUnaLinea(sueltasNuevas);
		if (antesJuntas.price !== ahoraJuntas.price) {
			if (nombreComparable(antesJuntas.name) !== nombreComparable(ahoraJuntas.name)) {
				ahoraJuntas.name = antesJuntas.name + ' → ' + ahoraJuntas.name;
			}
			ahoraJuntas.mezcla = ahoraJuntas.mezcla || antesJuntas.mezcla || antesJuntas.group !== ahoraJuntas.group;
			cambios.push({ antes: antesJuntas, ahora: ahoraJuntas });
		}
	}
	return cambios;
}

/**
 * Los opcionales PEDIDOS ({id, name, price}) frente a los que han quedado tras el tramo nuevo, con la
 * regla con la que se remapean (`opcionalesDelTramo`, N-286): el que sigue con su id, nada; el que pasó
 * a otro del mismo nombre cambia de precio si cuesta otra cosa; el que no tiene ninguno, se quita.
 */
export function opcionalesQueCambian(pedidos: any[], quedan: any[]): { quitados: any[]; cambiados: any[] } {
	const quitados: any[] = [];
	const cambiados: any[] = [];
	for (const pedido of pedidos || []) {
		const ahora = opcionalesDelTramo([pedido], quedan || [])[0];
		if (!ahora) {
			quitados.push(pedido);
		} else if (ahora.id != pedido.id && pedido.price != null && Number(ahora.price) !== Number(pedido.price)) {
			cambiados.push({ antes: pedido, ahora });
		}
	}
	return { quitados, cambiados };
}

/** «el grupo» o «por persona», según la tarifa de actividad (`group`); nada si junta de los dos tipos. */
export function unidadDeActividad(tarifa: any): string {
	if (tarifa && tarifa.mezcla) {
		return '';
	}
	return Number(tarifa && tarifa.group) === 1 ? 'el grupo' : 'por persona';
}
